import type z from 'zod';
import type { PaginationType } from '../types/Pagination.types';
import type {
  AllowedSendMarketingEmailsRequest,
  SecurityFormValues,
  SubscribePushNotificationsDto,
  UpdateUserDto,
  UserDto,
  UserPaymentMethodDto,
  VerifyPaymentMethodResponse,
  VpnDetectedMessage,
} from './user.schema';
import type {
  AccessTokenDTO,
} from '@/queries/auth/auth.schema';
import { logoutUser } from '@/libs/auth/logoutUser';
import { ENDPOINTS } from '@/queries/endpoints';
import { validateWithZod } from '@/utils/zod.utils';
import { httpClient } from '../httpClient';
import { tokenManager } from '../token-manager';
import { PaginationSchema } from '../types/Pagination.types';
import { AllowedSendMarketingEmailsRequestSchema, SecurityFormValuesSchema, UpdateUserDtoSchema, UserDtoSchema, UserPaymentMethodDtoSchema, VerifyPaymentMethodResponseSchema, VpnDetectedMessageSchema } from './user.schema';

const PAYMENT_METHODS_PAGE_SIZE = 20;

export const userApi = {
  async getUserMe(): Promise<UserDto> {
    const decoded = tokenManager.decodeJWT();
    const response = await httpClient.get<UserDto>(ENDPOINTS.user.me, { params: { id: decoded?.sub } });
    const user = validateWithZod(UserDtoSchema, response);

    if (user.deactivatedAt !== null) {
      tokenManager.removeTokens();
      logoutUser(false, true);
    }

    return user;
  },

  async patchUserMe(data: UpdateUserDto) {
    const validatedData = validateWithZod(UpdateUserDtoSchema, data);

    const jwt = tokenManager.decodeJWT();

    const response = await httpClient.patch<UserDto>(ENDPOINTS.user.me, validatedData, { params: { id: jwt?.sub } });
    return validateWithZod(UserDtoSchema, response);
  },

  async changeUserPassword(data: SecurityFormValues) {
    const validatedData = SecurityFormValuesSchema.parse(data);

    const jwt = tokenManager.decodeJWT();

    await httpClient.post(ENDPOINTS.user.changePassword, validatedData, { params: { id: jwt?.sub } });
  },

  async getUserPaymentMethods(): Promise<PaginationType<UserPaymentMethodDto>> {
    const { sub: userId } = tokenManager.decodeJWT() as AccessTokenDTO;

    const response = await httpClient.get<PaginationType<z.infer<typeof UserPaymentMethodDtoSchema>>>(
      ENDPOINTS.user.paymentMethods(userId),
      { params: { page: 1, limit: PAYMENT_METHODS_PAGE_SIZE } },
    );

    const result = validateWithZod(PaginationSchema(UserPaymentMethodDtoSchema), response);

    if (result?.hasNextPage) {
      console.warn(
        `[userApi.getUserPaymentMethods] More than ${PAYMENT_METHODS_PAGE_SIZE} payment methods returned; the list is truncated.`,
        { totalItems: result.totalItems },
      );
    }

    return result;
  },

  async verifyPaymentMethod(userId: string, cardId: string): Promise<VerifyPaymentMethodResponse> {
    const response = await httpClient.post<VerifyPaymentMethodResponse>(
      ENDPOINTS.user.verifyPaymentMethod(userId, cardId),
    );

    return validateWithZod(VerifyPaymentMethodResponseSchema, response);
  },

  async subscribePushNotifications(data: SubscribePushNotificationsDto): Promise<void> {
    await httpClient.post<void>(ENDPOINTS.user.pushNotificationsSubscribe, data);
  },

  async setAllowedSendMarketingEmails(data: AllowedSendMarketingEmailsRequest): Promise<UserDto> {
    const validatedData = validateWithZod(AllowedSendMarketingEmailsRequestSchema, data);
    const response = await httpClient.post<UserDto>(ENDPOINTS.user.allowedSendMarketingEmails, { isAllowed: validatedData.isAllowed });
    return validateWithZod(UserDtoSchema, response);
  },

  async trackAppInstall(): Promise<void> {
    await httpClient.post<void>(ENDPOINTS.user.appInstall);
  },

  async checkVpn(fingerprintRequestId: string): Promise<VpnDetectedMessage> {
    const response = await httpClient.post<VpnDetectedMessage>(
      ENDPOINTS.userDevices.vpnCheck,
      undefined,
      { headers: { 'x-fingerprint-request-id': fingerprintRequestId } },
    );

    return validateWithZod(VpnDetectedMessageSchema, response);
  },
};
